import type {
  DomainEventPublisher,
  InvoiceFinancials,
  InvoiceNumberGenerator,
} from "@invoicing-sdk/domain";
import {
  Invoice,
  InvoiceAlreadyExistsError,
  InvoiceDraft,
  InvoiceStatus,
  OrderNotFoundError,
} from "@invoicing-sdk/domain";
import type { InvoiceRepository, OrderRepository } from "../ports/index.js";
import type {
  RequestInvoiceCommand,
  RequestInvoiceResult,
} from "./commands.js";
import { InvoiceDraftMapper } from "./mappers/InvoiceDraftMapper.js";

/**
 * Dependencies required for RequestInvoice use case
 */
export interface RequestInvoiceDependencies {
  readonly orderRepository: OrderRepository;
  readonly invoiceRepository: InvoiceRepository;
  readonly invoiceNumberGenerator: InvoiceNumberGenerator;
  readonly eventPublisher?: DomainEventPublisher;
}

/**
 * Use case for requesting an invoice draft preparation
 * This prepares invoice data without finalizing it, allowing review before creation
 *
 * Requirements: 1.1 - Allow creation of invoice draft from order
 */
export function makeRequestInvoice(deps: RequestInvoiceDependencies) {
  return async (
    command: RequestInvoiceCommand
  ): Promise<RequestInvoiceResult> => {
    const { orderRepository, invoiceRepository, invoiceNumberGenerator } = deps;
    const { orderId } = command;

    // Check if order exists
    const order = await orderRepository.findById(orderId);
    if (!order) {
      throw new OrderNotFoundError(orderId);
    }

    // Check if invoice already exists for this order
    const existingInvoice = await invoiceRepository.findByOrderId(orderId);
    if (existingInvoice) {
      throw new InvoiceAlreadyExistsError(orderId);
    }

    // Draft invoice creation
    const financials: InvoiceFinancials = {
      subtotal: order.subtotal,
      taxes: order.taxes,
      shippingCost: order.shippingCost,
      discount: order.discount,
      grandTotal: order.subtotal
        .add(order.taxes)
        .add(order.shippingCost)
        .subtract(order.discount),
    };
    const financialsPO = {
      subtotal: financials.subtotal.toPlainObject(),
      taxes: financials.taxes.toPlainObject(),
      shippingCost: financials.shippingCost.toPlainObject(),
      discount: financials.discount.toPlainObject(),
      grandTotal: financials.grandTotal.toPlainObject(),
    };

    const draft = await Invoice.createDraft(
      {
        orderId: order.orderId,
        customerSnapshot: order.customer,
        itemsSnapshot: order.items,
        financials: financials,
        createdBy: command.requestedBy,
      },
      { invoiceNumberGenerator }
    );

    // Persist the invoice
    await invoiceRepository.save(draft);

    // Create the draft DTO using the mapper
    const draftDTO = InvoiceDraftMapper.toDTO(draft);

    // Prepare invoice data from order snapshot (maintaining backwards compatibility)
    const result: RequestInvoiceResult = {
      orderId: order.orderId,
      suggestedInvoiceNumber: draftDTO.invoiceNumber,
      customerInfo: draftDTO.customerSnapshot,
      items: draftDTO.itemsSnapshot,
      financials: draftDTO.financials,
      draft: draftDTO,
    };

    if (deps.eventPublisher) {
      draft.publishAllUncommitted(deps.eventPublisher);
    }

    return result;
  };
}

/**
 * Factory function to create RequestInvoice use case with dependencies
 */
export function createRequestInvoiceUseCase(
  deps: RequestInvoiceDependencies
): (command: RequestInvoiceCommand) => Promise<RequestInvoiceResult> {
  return makeRequestInvoice(deps);
}
